// Order status constants and utilities

import type { ColorValue } from '../list-builder/types/display';

export enum OrderStatus {
	UNSUBMITTED = '1',
	PENDING = '2',
	SENT_TO_ERP = '3',
	APPROVED = '4',
	PARTIALLY_SHIPPED = 'Partially Shipped',
	SHIPPED = '5',
	HELD = '6',
	CANCELLED = '7',
	APPROVAL_REQUIRED = '8',
	OVERRIDE_REJECTED = '9',
	CANCELLATION_REQUESTED = '10',
	OVERRIDE_APPROVED = '11',
	SUBMITTED = '12',
	QUOTE = '13',
	QUOTE_NEEDS_REVIEW = 'Quote - Needs Review',
	// Pre-approval hold for the order-day license check (PD-14806). Kept out of
	// DEFAULT_SUBMISSION_STATUS_OPTIONS so an order parked here is invisible to the
	// ERP until every stamped license is verified good as of today.
	PENDING_LICENSE_CHECK = 'Pending License Check',
}

export enum OrderAlertStatus {
	OK = '1',
	WARNING = '2',
	ERROR = '3',
}

export const OrderStatusLabels: Record<OrderStatus, string> = {
	[OrderStatus.UNSUBMITTED]: 'Unsubmitted',
	[OrderStatus.PENDING]: 'Pending',
	[OrderStatus.SENT_TO_ERP]: 'Sent to ERP',
	[OrderStatus.APPROVED]: 'Approved',
	[OrderStatus.PARTIALLY_SHIPPED]: 'Partially Shipped',
	[OrderStatus.SHIPPED]: 'Shipped',
	[OrderStatus.HELD]: 'Held',
	[OrderStatus.CANCELLED]: 'Cancelled',
	[OrderStatus.APPROVAL_REQUIRED]: 'Approval Required',
	[OrderStatus.OVERRIDE_REJECTED]: 'Override Rejected',
	[OrderStatus.CANCELLATION_REQUESTED]: 'Cancellation Requested',
	[OrderStatus.OVERRIDE_APPROVED]: 'Override Approved',
	[OrderStatus.SUBMITTED]: 'Submitted',
	[OrderStatus.QUOTE]: 'Quote',
	[OrderStatus.QUOTE_NEEDS_REVIEW]: 'Quote - Needs Review',
	[OrderStatus.PENDING_LICENSE_CHECK]: 'Pending License Check',
};

/**
 * Badge configuration for order statuses.
 *
 * A status names what it MEANS; the colour comes from the shared token layer
 * at render time. That is what keeps an order status looking the same in a
 * list row as it does on the record, and what lets a palette decision reach
 * both without either being edited.
 */
export type OrderStatusBadgeColors = ColorValue;

/**
 * Centralized meaning mappings for all order statuses.
 * This is the single source of truth for status badges throughout the application.
 *
 * Statuses that share a meaning share a colour — `Held`, `Cancelled` and
 * `Override Rejected` all read as danger. They were already near-identical
 * reds, and every badge carries its own word, so the label is what separates
 * them rather than a hue nobody could name.
 */
export const OrderStatusColors: Record<string, OrderStatusBadgeColors> = {
	[OrderStatus.UNSUBMITTED]: { type: 'neutral' },
	[OrderStatus.PENDING]: { type: 'warning' },
	[OrderStatus.SENT_TO_ERP]: { type: 'info' },
	[OrderStatus.APPROVED]: { type: 'success' },
	[OrderStatus.PARTIALLY_SHIPPED]: { type: 'info' },
	[OrderStatus.SHIPPED]: { type: 'info' },
	[OrderStatus.HELD]: { type: 'danger' },
	[OrderStatus.CANCELLED]: { type: 'danger' },
	[OrderStatus.APPROVAL_REQUIRED]: { type: 'warning' },
	[OrderStatus.OVERRIDE_REJECTED]: { type: 'danger' },
	[OrderStatus.CANCELLATION_REQUESTED]: { type: 'warning' },
	[OrderStatus.OVERRIDE_APPROVED]: { type: 'success' },
	[OrderStatus.SUBMITTED]: { type: 'success' },
	[OrderStatus.QUOTE]: { type: 'info' },
	[OrderStatus.QUOTE_NEEDS_REVIEW]: { type: 'warning' },
};

/**
 * Default used when a status is not recognized.
 */
export const DEFAULT_ORDER_STATUS_COLORS: OrderStatusBadgeColors = {
	type: 'neutral',
};

/**
 * Gets the badge colors for a given order status.
 * Returns the centralized color configuration for consistent styling across the application.
 *
 * @param status - The status code from the database
 * @returns Badge colors (background and text) for the status
 */
export function getOrderStatusBadgeColors(
	status?: string,
): OrderStatusBadgeColors {
	if (status == null) {
		return DEFAULT_ORDER_STATUS_COLORS;
	}

	return OrderStatusColors[status] ?? DEFAULT_ORDER_STATUS_COLORS;
}

/**
 * The `Badge` variant for a status, derived from the SAME meaning map the
 * lists use. Anywhere that renders a `Badge` rather than going through the
 * list-builder display pipeline reaches for this, so the two cannot drift.
 */
export function getOrderStatusBadgeVariant(
	status?: string,
): 'success' | 'warning' | 'danger' | 'info' | 'primary' | 'default' {
	const intent = getOrderStatusBadgeColors(status).type;

	switch (intent) {
		case 'success':
		case 'warning':
		case 'danger':
		case 'info':
		case 'primary':
			return intent;
		default:
			return 'default';
	}
}

/**
 * Maps an order status code to its human-readable label
 * @param status - The status code from the database
 * @returns The human-readable status label
 */
export function getOrderStatusLabel(status?: string): string {
	if (status == null || status === '') {
		return 'Unknown';
	}

	const orderStatus = status as OrderStatus;

	return OrderStatusLabels[orderStatus] ?? status;
}

/**
 * Gets the color variant for a given order status
 * @param status - The status code from the database
 * @returns Color variant for styling
 * @deprecated Use getOrderStatusBadgeColors() for consistent badge colors across the app
 */
export function getOrderStatusColor(
	status?: string,
): 'primary' | 'secondary' | 'warning' | 'danger' | 'info' | 'success' {
	if (status == null) {
		return 'secondary';
	}

	switch (status as OrderStatus) {
		case OrderStatus.UNSUBMITTED:
			return 'secondary';
		case OrderStatus.PENDING:
			return 'warning';
		case OrderStatus.SENT_TO_ERP:
			return 'info';
		case OrderStatus.APPROVED:
			return 'primary';
		case OrderStatus.PARTIALLY_SHIPPED:
			return 'warning';
		case OrderStatus.SHIPPED:
			return 'success';
		case OrderStatus.HELD:
			return 'warning';
		case OrderStatus.CANCELLED:
			return 'danger';
		case OrderStatus.APPROVAL_REQUIRED:
			return 'warning';
		case OrderStatus.OVERRIDE_REJECTED:
			return 'danger';
		case OrderStatus.CANCELLATION_REQUESTED:
			return 'warning';
		case OrderStatus.OVERRIDE_APPROVED:
			return 'primary';
		case OrderStatus.SUBMITTED:
			return 'primary';
		case OrderStatus.QUOTE:
			return 'info';
		case OrderStatus.QUOTE_NEEDS_REVIEW:
			return 'warning';
		default:
			return 'secondary';
	}
}

/**
 * Checks if an order status is considered active (not cancelled or fully completed)
 * @param status - The status code from the database
 * @returns True if the order is active
 */
export function isOrderActive(status?: string): boolean {
	if (status == null) {
		return false;
	}

	return (
		status === OrderStatus.UNSUBMITTED ||
		status === OrderStatus.PENDING ||
		status === OrderStatus.SENT_TO_ERP ||
		status === OrderStatus.APPROVED ||
		status === OrderStatus.PARTIALLY_SHIPPED ||
		status === OrderStatus.HELD ||
		status === OrderStatus.APPROVAL_REQUIRED ||
		status === OrderStatus.OVERRIDE_APPROVED ||
		status === OrderStatus.SUBMITTED
	);
}

/**
 * Checks if an order is completed (fully shipped)
 * @param status - The status code from the database
 * @returns True if the order is completed
 */
export function isOrderCompleted(status?: string): boolean {
	return status === OrderStatus.SHIPPED;
}

/**
 * Checks if an order is cancelled or rejected
 * @param status - The status code from the database
 * @returns True if the order is cancelled or rejected
 */
export function isOrderCancelled(status?: string): boolean {
	return (
		status === OrderStatus.CANCELLED ||
		status === OrderStatus.OVERRIDE_REJECTED ||
		status === OrderStatus.CANCELLATION_REQUESTED
	);
}

/**
 * Checks if an order is a quote
 * @param status - The status code from the database
 * @returns True if the order is a quote
 */
export function isOrderQuote(status?: string): boolean {
	return (
		status === OrderStatus.QUOTE || status === OrderStatus.QUOTE_NEEDS_REVIEW
	);
}

/**
 * Gets all available order statuses for dropdowns/selects
 * @returns Array of status options with value and label
 */
export function getOrderStatusOptions(): Array<{
	value: OrderStatus;
	label: string;
}> {
	return Object.entries(OrderStatusLabels).map(([value, label]) => ({
		value: value as OrderStatus,
		label,
	}));
}

/**
 * A single selectable order-status option, mirroring the shape returned by the
 * `/orders/distinct-statuses` endpoint: `id` is the raw value stored in
 * `order.status`, `label` is the human-readable name (for tenant custom
 * statuses the two are identical).
 */
export interface OrderStatusOptionModel {
	id: string;
	label: string;
}

/**
 * Resolve a free-form status string (as supplied by an AI agent or a user) to
 * the matching DB status id(s) actually stored in `order.status`.
 *
 * Matching is case-insensitive and accepts any of:
 *   - the raw DB code            — e.g. '12'
 *   - the OrderStatus enum name  — e.g. 'SUBMITTED', 'APPROVAL_REQUIRED'
 *   - the human label            — e.g. 'Submitted', 'Approval Required'
 *   - a tenant custom status     — matched against the supplied `tenantOptions`
 *                                  (custom statuses store id === label)
 *   - the synthetic "requires attention" bucket → 'requires-attention'
 *
 * `tenantOptions` should be the live distinct-statuses list (the same source
 * the UI status dropdown uses); pass it so tenant-specific custom statuses
 * resolve. The standard OrderStatus set always resolves regardless of whether
 * those statuses are currently present in the tenant's data.
 *
 * Returns the de-duplicated matching ids, or an empty array when nothing
 * matches — callers should treat empty as "unknown status".
 */
export function resolveOrderStatusIds(
	input: string | null | undefined,
	tenantOptions: Array<OrderStatusOptionModel> = [],
): Array<string> {
	if (input == null) {
		return [];
	}

	const normalized = input.trim().toLowerCase();

	if (normalized === '') {
		return [];
	}

	// The synthetic "Requires Attention" bucket is handled specially by the
	// order filter builder (status IN (Held, Approval Required) OR alert error),
	// so map any phrasing of it to that sentinel id.
	if (
		normalized === 'requires attention' ||
		normalized === 'requires-attention'
	) {
		return ['requires-attention'];
	}

	const matches = new Set<string>();

	// Standard statuses: match against the DB code, the enum name, and the label.
	for (const [name, code] of Object.entries(OrderStatus)) {
		const label = OrderStatusLabels[code as OrderStatus] ?? code;

		if (
			normalized === code.toLowerCase() ||
			normalized === name.toLowerCase() ||
			normalized === label.toLowerCase()
		) {
			matches.add(code);
		}
	}

	// Tenant statuses (including custom ones not in the standard enum): match
	// against the id and label.
	for (const option of tenantOptions) {
		if (
			normalized === option.id.toLowerCase() ||
			normalized === option.label.toLowerCase()
		) {
			matches.add(option.id);
		}
	}

	return Array.from(matches);
}

/**
 * Compute the default "active orders" inclusion set for order search: every
 * status currently present for the tenant EXCEPT carts (Unsubmitted), quotes,
 * the synthetic requires-attention bucket, and the cancelled family (Cancelled
 * / Override Rejected / Cancellation Requested). Custom tenant statuses are
 * treated as active and kept.
 *
 * The order query pipeline only supports status *inclusion* (`status IN (...)`),
 * so "exclude cancelled by default" is expressed as "include everything active".
 * Used when an order search supplies no explicit status, so a plain
 * "find recent orders" query doesn't surface cancelled orders.
 */
export function getDefaultActiveOrderStatusIds(
	tenantOptions: Array<OrderStatusOptionModel>,
): Array<string> {
	return tenantOptions
		.map((option) => option.id)
		.filter(
			(id) =>
				id !== 'requires-attention' &&
				id !== OrderStatus.UNSUBMITTED &&
				id !== OrderStatus.QUOTE &&
				id !== OrderStatus.QUOTE_NEEDS_REVIEW &&
				!isOrderCancelled(id),
		);
}
